/**
 * #1113 — the deployed defaults the Finance config cards show for a key with no override. The
 * dynamic-config API deliberately does not report deployed values, so these mirror the services'
 * code and `financeConfigDefaults.test.ts` pins them:
 *  - service fee: orders `ServiceFeeProperties` (off, 0, 0, no cap);
 *  - Tabby: billing `BillingDynamicSettings` (L92–L137): customer switch off, no pilot list, 6.99 % +
 *    AED 1.00, VAT on top, paid by the garage on the full payment, no minimum order, 7 hold days, no
 *    payout fee;
 *  - Zofeur (#1251): orders `DeliveryProperties` (Zofeur option off, insurance note off);
 *  - dealer credit (Spec 085): orders `DealerCreditSettings` (3 months live, 12 months unassigned,
 *    14 days to auto-revoke, KEEP on expiry, 14 days restored on refund, no sale VAT,
 *    BREAKAGE_INCOME, AED 100 minimum). Its kill switch `garagat.dealer-credit.enabled` is a
 *    `dynamic-config` key, not one of these.
 */

export const SERVICE_FEE_KEYS = {
  enabled: 'garagat.service-fee.enabled',
  percent: 'garagat.service-fee.percent',
  fixedAed: 'garagat.service-fee.fixed-aed',
  capAed: 'garagat.service-fee.cap-aed',
} as const;

export const TABBY_KEYS = {
  customerEnabled: 'garagat.payment.tabby.customer-enabled',
  pilotCustomerIds: 'garagat.payment.tabby.pilot-customer-ids',
  minOrderAmount: 'garagat.payment.tabby.min-order-amount',
  feePercent: 'garagat.payment.tabby.fee-percent',
  feeFixedAed: 'garagat.payment.tabby.fee-fixed-aed',
  feeVatInclusive: 'garagat.payment.tabby.fee-vat-inclusive',
  feeBearer: 'garagat.payment.tabby.fee-bearer',
  feeGarageBase: 'garagat.payment.tabby.fee-garage-base',
  settlementHoldDays: 'garagat.payment.tabby.settlement-hold-days',
  payoutFeeAed: 'garagat.payment.tabby.payout-fee-aed',
} as const;

export const ZOFEUR_KEYS = {
  optionEnabled: 'garagat.delivery.zofeur-option.enabled',
  insuranceNoteEnabled: 'garagat.delivery.insurance-disclaimer.enabled',
} as const;

export const DEALER_CREDIT_KEYS = {
  validityMonths: 'garagat.dealer-credit.validity-months',
  unassignedMonths: 'garagat.dealer-credit.unassigned-months',
  autoRevokeDays: 'garagat.dealer-credit.auto-revoke-days',
  expiryDisposition: 'garagat.dealer-credit.expiry-disposition',
  refundRestoreMinDays: 'garagat.dealer-credit.refund-restore-min-days',
  saleVatRate: 'garagat.dealer-credit.sale-vat-rate',
  expiryTreatment: 'garagat.dealer-credit.expiry-treatment',
  minAmountAed: 'garagat.dealer-credit.min-amount-aed',
} as const;

export const SERVICE_FEE_DEFAULTS: Record<string, string> = {
  [SERVICE_FEE_KEYS.enabled]: 'false',
  [SERVICE_FEE_KEYS.percent]: '0',
  [SERVICE_FEE_KEYS.fixedAed]: '0',
  [SERVICE_FEE_KEYS.capAed]: '0',
};

export const TABBY_DEFAULTS: Record<string, string> = {
  [TABBY_KEYS.customerEnabled]: 'false',
  [TABBY_KEYS.pilotCustomerIds]: '',
  [TABBY_KEYS.minOrderAmount]: '0',
  [TABBY_KEYS.feePercent]: '6.99',
  [TABBY_KEYS.feeFixedAed]: '1.00',
  [TABBY_KEYS.feeVatInclusive]: 'false',
  [TABBY_KEYS.feeBearer]: 'GARAGAT',
  [TABBY_KEYS.feeGarageBase]: 'FULL_PAYMENT',
  [TABBY_KEYS.settlementHoldDays]: '7',
  [TABBY_KEYS.payoutFeeAed]: '0',
};

export const ZOFEUR_DEFAULTS: Record<string, string> = {
  [ZOFEUR_KEYS.optionEnabled]: 'false',
  [ZOFEUR_KEYS.insuranceNoteEnabled]: 'false',
};

export const DEALER_CREDIT_DEFAULTS: Record<string, string> = {
  [DEALER_CREDIT_KEYS.validityMonths]: '3',
  [DEALER_CREDIT_KEYS.unassignedMonths]: '12',
  [DEALER_CREDIT_KEYS.autoRevokeDays]: '14',
  [DEALER_CREDIT_KEYS.expiryDisposition]: 'KEEP',
  [DEALER_CREDIT_KEYS.refundRestoreMinDays]: '14',
  [DEALER_CREDIT_KEYS.saleVatRate]: '0',
  [DEALER_CREDIT_KEYS.expiryTreatment]: 'BREAKAGE_INCOME',
  [DEALER_CREDIT_KEYS.minAmountAed]: '100',
};

/** Every key a Finance card edits, by the screen the backend gates it with (DynamicConfigKey.adminScreen). */
export const FINANCE_CONFIG_KEYS: string[] = [
  ...Object.values(SERVICE_FEE_KEYS), ...Object.values(TABBY_KEYS), ...Object.values(ZOFEUR_KEYS),
  ...Object.values(DEALER_CREDIT_KEYS),
];
